import { useCallback, useState } from 'react';
import { Box } from '@mui/material';
import CalendarToolbar from './components/CalendarToolbar';
import CreateWorkboardTaskModal from './components/CreateWorkboardTaskModal';
import ModelsCalendar from './ModelsCalendar';
import { ModelsCalendarProps } from './ModelsCalendar.types';
import { CALENDAR_DATE_KEY, CALENDAR_VIEW_KEY, CALENDAR_VIEWS } from '../../common/constants/calendar';
import { useCalendarState } from '../../hooks/useCalendarState';

type ModelsCalendarWithToolbarProps = Omit<
  ModelsCalendarProps,
  'hideToolbar' | 'controlledDate' | 'controlledView' | 'onDateChange' | 'onViewChange' | 'onDayDoubleClick' | 'onWorkboardTaskSelect'
>;

const ModelsCalendarWithToolbar = (props: ModelsCalendarWithToolbarProps) => {
  const { calendarDate, setCalendarDate, calendarView, handleCalendarNavigate, handleCalendarViewChange } =
    useCalendarState({ dateStorageKey: CALENDAR_DATE_KEY, viewStorageKey: CALENDAR_VIEW_KEY });

  const [isCreateTaskModalOpen, setIsCreateTaskModalOpen] = useState(false);
  const [createTaskDefaultDate, setCreateTaskDefaultDate] = useState<Date | null>(null);
  const [editingTaskId, setEditingTaskId] = useState<number | null>(null);
  const [editingOccurrenceDate, setEditingOccurrenceDate] = useState<string | null>(null);

  const handleCalendarDateSelect = useCallback((newDate: Date) => {
    setCalendarDate(newDate);
  }, [setCalendarDate]);

  const closeTaskModal = useCallback(() => {
    setIsCreateTaskModalOpen(false);
    setEditingTaskId(null);
    setEditingOccurrenceDate(null);
  }, []);

  const openCreateTaskModal = useCallback((date?: Date) => {
    setEditingTaskId(null);
    setCreateTaskDefaultDate(date ?? calendarDate);
    setIsCreateTaskModalOpen(true);
  }, [calendarDate]);

  const handleCreateTaskFromToolbar = useCallback(() => {
    openCreateTaskModal(new Date());
  }, [openCreateTaskModal]);

  const handleDayDoubleClick = useCallback((date: Date) => {
    openCreateTaskModal(date);
  }, [openCreateTaskModal]);

  const handleWorkboardTaskSelect = useCallback((taskId: number, occurrenceDate: string) => {
    setIsCreateTaskModalOpen(false);
    setEditingTaskId(taskId);
    setEditingOccurrenceDate(occurrenceDate);
  }, []);

  const isTaskModalOpen = isCreateTaskModalOpen || editingTaskId != null;

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        flex: 1,
        minHeight: 0,
        overflow: 'hidden',
      }}
    >
      <Box
        sx={{
          position: 'sticky',
          top: 0,
          zIndex: 2,
          flexShrink: 0,
          backgroundColor: 'background.paper',
        }}
      >
        <CalendarToolbar
          date={calendarDate}
          view={calendarView}
          onNavigate={handleCalendarNavigate}
          onViewChange={handleCalendarViewChange}
          onDateSelect={handleCalendarDateSelect}
          onCreateTask={handleCreateTaskFromToolbar}
          views={[...CALENDAR_VIEWS]}
        />
      </Box>
      <Box sx={{ flex: 1, minHeight: 0, overflow: 'auto' }}>
        <ModelsCalendar
          {...props}
          hideToolbar
          controlledDate={calendarDate}
          controlledView={calendarView}
          onDateChange={setCalendarDate}
          onViewChange={handleCalendarViewChange}
          onDayDoubleClick={handleDayDoubleClick}
          onWorkboardTaskSelect={handleWorkboardTaskSelect}
        />
      </Box>
      <CreateWorkboardTaskModal
        open={isTaskModalOpen}
        onClose={closeTaskModal}
        defaultStartsOn={createTaskDefaultDate}
        taskId={editingTaskId}
        occurrenceDate={editingOccurrenceDate}
      />
    </Box>
  );
};

export default ModelsCalendarWithToolbar;
